/**
 * SettingsLayoutRoute — chrome for /settings/*. Renders the AppRail on
 * the left, AppTopBar on top, and a horizontal sub-nav (General · Team
 * · Users · Plan · Usage · Audit) above the page content. Each child
 * page returns just its content; this layout owns the chrome so
 * navigation between sub-tabs doesn't remount the rail.
 */

import { Suspense } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  AppRail, AppTopBar, useRailCollapsed,
  APP_C as C, APP_MONO as MONO,
} from '../../main/components/AppRail';
import { useAuth } from '../../auth';
import { hasRole } from '../../../shared/utils';

interface Tab {
  key: string;
  label: string;
  href: string;
  /** When set, only users with one of these role.level pairs see the tab. */
  requireRoles?: string[];
  /** Hide for platform admins (admin.system). Plan / Team / LLM usage are
   *  per-tenant concepts that don't apply to a super-admin view. */
  hideForPlatformAdmin?: boolean;
  /** Show ONLY for white-label partner logins (carry a partnerId). */
  partnerOnly?: boolean;
  /** Hide on white-label instances (testmug / packetprep). These are
   *  fresherbot-specific surfaces (AI onboarding, LLM usage, practice plans,
   *  help) that the slim institution experience doesn't expose. */
  hideForWhiteLabel?: boolean;
  /** Show a small BETA badge next to the label. */
  beta?: boolean;
}

const IS_WHITELABEL =
  ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}).VITE_WHITELABEL === 'true';

const ALL_TABS: Tab[] = [
  { key: 'general', label: 'General',   href: '/settings' },
  { key: 'college', label: 'Onboarding', href: '/settings/college',
    requireRoles: ['admin.tenant', 'admin.org', 'admin.system'],
    hideForPlatformAdmin: true, hideForWhiteLabel: true },
  { key: 'partner-users', label: 'Partner Users', href: '/settings/partner-users', partnerOnly: true },
  { key: 'super-users', label: 'Super Users', href: '/settings/super-users',
    requireRoles: ['admin.system'] },
  { key: 'branding', label: 'Branding', href: '/settings/branding',
    requireRoles: ['admin.system'] },
  { key: 'pages', label: 'Pages', href: '/settings/pages',
    requireRoles: ['admin.system', 'admin.org'] },
  { key: 'blog', label: 'Blog', href: '/platform/blog',
    requireRoles: ['admin.system'] },
  { key: 'plan',    label: 'Plan',      href: '/settings/plan',
    requireRoles: ['admin.tenant', 'admin.org', 'admin.system', 'moderator.manage'],
    hideForPlatformAdmin: true },
  { key: 'team',    label: 'Team',      href: '/settings/team',
    requireRoles: ['admin.tenant', 'admin.org', 'admin.system', 'moderator.manage'],
    hideForPlatformAdmin: true },
  { key: 'usage',   label: 'LLM usage', href: '/settings/usage',
    requireRoles: ['admin.tenant', 'admin.org', 'admin.system'],
    hideForPlatformAdmin: true, hideForWhiteLabel: true },
  // Practice Plans — beta; surfaced here (not the main rail) while it bakes.
  // Links to the real /plans page (no settings stub).
  { key: 'plans',   label: 'Practice Plans', href: '/plans', beta: true,
    requireRoles: ['admin.tenant', 'admin.org', 'admin.system', 'moderator.manage'],
    hideForPlatformAdmin: true, hideForWhiteLabel: true },
  // Help — moved out of the main rail into Settings; links to /help.
  { key: 'help',    label: 'Help', href: '/help',
    hideForPlatformAdmin: true, hideForWhiteLabel: true },
];

function activeKey(pathname: string): string {
  if (pathname.startsWith('/settings/college')) return 'college';
  if (pathname.startsWith('/settings/partner-users')) return 'partner-users';
  if (pathname.startsWith('/settings/super-users')) return 'super-users';
  if (pathname.startsWith('/settings/branding')) return 'branding';
  if (pathname.startsWith('/settings/pages')) return 'pages';
  if (pathname.startsWith('/settings/team')) return 'team';
  if (pathname.startsWith('/settings/plan')) return 'plan';
  if (pathname.startsWith('/settings/usage')) return 'usage';
  return 'general';
}

export function SettingsLayoutRoute() {
  const { collapsed, toggle } = useRailCollapsed();
  const { user } = useAuth();
  const location = useLocation();
  const active = activeKey(location.pathname);

  const isPlatformAdmin =
    !!user && (user as any).role === 'admin' && (user as any).level === 'system';
  // White-label partner logins get a stripped Settings: General + Partner Users.
  const isPartner = !isPlatformAdmin && !!(user as any)?.partnerId;
  const isReviewer = user?.role === 'moderator' && (user as any)?.level === 'review';
  const tabs = ALL_TABS.filter((t) => {
    if (isPartner) return t.key === 'general' || t.partnerOnly || (t.key === 'pages' && !!user && hasRole(user, t.requireRoles ?? []));
    if (t.partnerOnly) return false;
    if (t.hideForWhiteLabel && IS_WHITELABEL) return false;
    if (t.requireRoles && !(user && hasRole(user, t.requireRoles))) return false;
    if (t.hideForPlatformAdmin && isPlatformAdmin) return false;
    if (t.key === 'help' && isReviewer) return false;
    return true;
  });

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: C.bg2 }}>
      <AppRail collapsed={collapsed} activeNav="settings" />
      <main
        className="flex-1 flex flex-col min-w-0"
        style={{ background: C.bg2 }}
      >
        <AppTopBar collapsed={collapsed} onToggle={toggle} title="Settings" />

        {/* Sub-nav pills */}
        <div
          className="flex items-center gap-1 px-6 py-2.5 overflow-x-auto fb-noscrollbar"
          style={{ background: C.surface, borderBottom: `1px solid ${C.line}` }}
        >
          {tabs.map((t) => {
            const on = active === t.key;
            return (
              <Link
                key={t.key}
                to={t.href}
                className="px-2.5 py-1 rounded-md text-[12.5px] font-medium whitespace-nowrap"
                style={{
                  background: on ? C.bg2 : 'transparent',
                  color: on ? C.ink : C.ink3,
                  fontFamily: on ? undefined : MONO,
                }}
              >
                {t.label}
                {t.beta && (
                  <span className="ml-1.5 px-1 py-0.5 rounded text-[8.5px] font-bold uppercase tracking-wider align-middle" style={{ background: C.infoSoft, color: C.info }}>Beta</span>
                )}
              </Link>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto">
          <Suspense fallback={<div className="p-6 text-sm" style={{ color: C.ink3 }}>Loading…</div>}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
}
